import React from 'react';
import PropTypes from 'prop-types';
import { Grid, Typography } from '@material-ui/core';
import {
  healthBasedLevelLow,
  healthBasedLevelSevere,
  healthBasedLevelCritical,
} from 'on_web/assets/imageList';
import { Trans, useTranslation } from 'react-i18next';
import {
  LOW_RISK_COLOR,
  SEVERE_RISK_COLOR,
  CRITICAL_RISK_COLOR,
} from 'colours';

/**
 * Component displaying summary for Health based risk.
 *
 * @param {Component properties} props
 */
export default function HealthBasedRiskSummary(props) {
  const { t } = useTranslation();

  const getCommorbiditiyRisk = value => {
    if (value === 0) {
      return {
        image: healthBasedLevelLow,
        color: LOW_RISK_COLOR,
        title: (
          <Trans
            i18nKey="reporting.comorbiditiesOverview.overallRiskMessage"
            values={{
              riskLevel: t('reporting.common.risks.low')
                .toUpperCase()
                .bold(),
            }}
          />
        ),
        text: t('reporting.myAssessment.percentageDescription'),
      };
    } else if (value === 1) {
      return {
        image: healthBasedLevelSevere,
        color: SEVERE_RISK_COLOR,
        title: (
          <Trans
            i18nKey="reporting.comorbiditiesOverview.overallRiskMessage"
            values={{
              riskLevel: t('reporting.common.risks.severe')
                .toUpperCase()
                .bold(),
            }}
          />
        ),
        text: t('reporting.myAssessment.percentageDescription'),
      };
    } else if (value === 2) {
      return {
        image: healthBasedLevelCritical,
        color: CRITICAL_RISK_COLOR,
        title: (
          <Trans
            i18nKey="reporting.comorbiditiesOverview.overallRiskMessage"
            values={{
              riskLevel: t('reporting.common.risks.critical')
                .toUpperCase()
                .bold(),
            }}
          />
        ),
        text: t('reporting.myAssessment.percentageDescription'),
      };
    }
  };

  /**
   * Gets a proper image based on user's risk value.
   */
  const getImage = () => {
    return (
      <img
        src={getCommorbiditiyRisk(props.healthRiskValue).image}
        height={props.isDesktop ? '120px' : '80px'}
        alt="Risk illustration"
      ></img>
    );
  };

  return (
    <>
      <Grid
        container
        direction="row"
        justify="space-around"
        alignItems="center"
      >
        <Grid item xs={props.isDesktop ? '12' : '8'}>
          <Typography
            style={{
              fontFamily: 'MuseoSans-500',
              fontSize: props.isDesktop ? '28px' : '20px',
              color: getCommorbiditiyRisk(props.healthRiskValue).color,
            }}
          >
            {getCommorbiditiyRisk(props.healthRiskValue).title}
          </Typography>
        </Grid>
        {props.isDesktop === false && (
          <Grid item xs={'4'}>
            {getImage()}
          </Grid>
        )}
      </Grid>

      <Grid
        container
        direction="row"
        justify={props.isDesktop ? 'space-around' : 'flex-start'}
        alignItems="flex-end"
        style={{ paddingTop: 20, paddingBottom: 20 }}
      >
        <Grid item>
          <Typography
            style={{
              fontFamily: 'Museo-900',
              fontSize: props.isDesktop ? '70px' : '48px',
              fontWeight: 900,
              color: getCommorbiditiyRisk(props.healthRiskValue).color,
            }}
          >
            {props.healthRiskScorePercentage}
          </Typography>
        </Grid>

        {props.isDesktop && <Grid item>{getImage()}</Grid>}
      </Grid>
      <Typography
        style={{
          fontFamily: 'Museo-500',
          fontSize: props.isDesktop ? '18px' : '16px',
          fontWeight: 500,
        }}
      >
        {getCommorbiditiyRisk(props.healthRiskValue).text}
      </Typography>
    </>
  );
}

HealthBasedRiskSummary.propTypes = {
  /**
   * Indicates Desktop resolution.
   */
  isDesktop: PropTypes.bool.isRequired,

  /**
   * Health risk.
   */
  healthRiskValue: PropTypes.number.isRequired,

  /**
   * Health risk score expressed as percentage.
   */
  healthRiskScorePercentage: PropTypes.string.isRequired,
};
